import React, { FC, useRef, useState } from 'react'
import { observer } from 'mobx-react'
import { RefresherEventDetail } from '@ionic/core'
import { RouteComponentProps } from 'react-router-dom'
import { IonContent, IonPage, IonRefresher, IonRefresherContent, IonInfiniteScroll, IonInfiniteScrollContent, useIonViewWillEnter } from '@ionic/react'
import { Header, MktEmpty } from '@cps'
import { ActivityAPI } from '@api/index'
import { storeMobx } from '@mobxStore'
import { useHistoryNew } from '@utils'
import { chevronDownCircleOutline } from 'ionicons/icons'
import defaultImg from '@img/default-longMallProduct.png'
import {
  formatDateRange,
  getActivityName,
  getBookingId,
  getCover,
  getTicketQuantity,
  normalizeList
} from '../utils'
import './index.scss'

interface IProps extends IPage, RouteComponentProps { }

const tabs = [
  { labelKey: 'mkt.activityReserve.status.unused', value: 'UNUSED' },
  { labelKey: 'mkt.activityReserve.status.used', value: 'USED' },
  { labelKey: 'mkt.activityReserve.status.refunded', value: 'REFUNDED' },
  { labelKey: 'mkt.activityReserve.status.expired', value: 'EXPIRED' },
]

const statusMap: Record<string, string[]> = {
  UNUSED: ['UNUSED'],
  USED: ['USED'],
  REFUNDED: ['REFUNDED'],
  EXPIRED: ['EXPIRED'],
}

const pageSize = 20

const getRecordStoreId = (item: any) => {
  if (typeof item?.store === 'number' || typeof item?.store === 'string') return item.store
  return item?.store?.storeId ||
    item?.store?.id ||
    item?.storeId ||
    item?.activityStoreId ||
    item?.booking?.storeId ||
    item?.order?.storeId ||
    item?.infos?.storeId
}

const getRecordStoreName = (item: any) => {
  const storeName = item?.store?.storeName ||
    item?.store?.name ||
    item?.storeName ||
    item?.booking?.storeName ||
    item?.order?.storeName ||
    item?.infos?.storeName
  if (storeName) return storeName
  const storeId = getRecordStoreId(item)
  if (!storeId) return ''
  const matchedStore: any = storeMobx.storeIdToStore?.[storeId] ||
    storeMobx.storeIdToStore?.[String(storeId)] ||
    storeMobx.storeIdToViewStore?.[storeId] ||
    storeMobx.storeIdToViewStore?.[String(storeId)] ||
    [...(storeMobx.viewStoreList || []), ...(storeMobx.storeList || []), storeMobx.info]
      .find((store: any) => String(store?.storeId || store?.id) === String(storeId))
  return matchedStore?.storeName || matchedStore?.name || ''
}

const getRecordSession = (item: any) => item?.session ||
  item?.schedule ||
  item?.activitySchedule ||
  item?.booking?.session ||
  item?.booking?.schedule ||
  item?.order?.session ||
  item?.order?.schedule ||
  {}

const getRecordSessionStartAt = (item: any) => {
  const session = getRecordSession(item)
  return session?.startAt ||
    session?.scheduleStartAt ||
    session?.beginAt ||
    session?.startTime ||
    item?.scheduleStartAt ||
    item?.activityScheduleStartAt ||
    item?.booking?.scheduleStartAt ||
    item?.order?.scheduleStartAt ||
    ''
}

const getRecordSessionEndAt = (item: any) => {
  const session = getRecordSession(item)
  return session?.endAt ||
    session?.scheduleEndAt ||
    session?.finishAt ||
    session?.endTime ||
    item?.scheduleEndAt ||
    item?.activityScheduleEndAt ||
    item?.booking?.scheduleEndAt ||
    item?.order?.scheduleEndAt ||
    ''
}

const ActivityReserveRecords: FC<IProps> = () => {
  const history = useHistoryNew()
  const [type, setType] = useState('UNUSED')
  const [list, setList] = useState<any[]>([])
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const typeRef = useRef(type)
  const requestIdRef = useRef(0)

  const mergeUniqueList = (data: any[]) => {
    const bookingIdSet = new Set()
    return data.filter((item: any) => {
      const bookingId = getBookingId(item)
      if (!bookingId) return true
      if (bookingIdSet.has(bookingId)) return false
      bookingIdSet.add(bookingId)
      return true
    })
  }

  const getData = (status = typeRef.current, pageNum = 1, append = false) => {
    const requestId = ++requestIdRef.current
    const statuses = statusMap[status] || [status]
    return Promise.all(statuses.map(bookingStatus => ActivityAPI.getActivityReserveRecords({
      bookingStatus,
      page: pageNum,
      pageSize,
    }))).then((results: any[]) => {
      // 只允许当前 tab 的最后一次请求回填，避免返回详情或快速切换时数据串到其他 tab。
      if (status !== typeRef.current || requestId !== requestIdRef.current) return
      const normalizedResults = results.map((res: any) => !res?.errors ? normalizeList(res) : [])
      const mergedList = normalizedResults.reduce((list: any[], items: any[]) => {
        list.push(...items)
        return list
      }, [])
      setPage(pageNum)
      setHasMore(normalizedResults.some((items: any[]) => items.length >= pageSize))
      setList(prev => mergeUniqueList(append ? prev.concat(mergedList) : mergedList))
    }).catch(() => {
      if (status !== typeRef.current || requestId !== requestIdRef.current) return
      if (!append) setList([])
      setHasMore(false)
    })
  }

  const doRefresh = (event: CustomEvent<RefresherEventDetail>) => {
    getData(typeRef.current, 1, false).finally(() => {
      event.detail.complete()
    })
  }

  const onScrollEnd = (ev: any) => {
    if (!hasMore) {
      ev.target.complete()
      return
    }
    getData(typeRef.current, page + 1, true).finally(() => {
      ev.target.complete()
    })
  }

  const changeTab = (status: string) => {
    typeRef.current = status
    setType(status)
    setPage(1)
    setHasMore(true)
    getData(status, 1, false)
  }

  useIonViewWillEnter(() => {
    getData(typeRef.current, 1, false)
  })

  return (
    <IonPage className='activity-reserve-records-page'>
      <Header onBack={() => history.goBack()}>
        {tabs.map(item => (
          <div key={item.value} className={`top-select ${type === item.value ? 'title' : 'desc'}`} onClick={() => changeTab(item.value)}>
            <span>{i18n.t(item.labelKey)}</span>
          </div>
        ))}
      </Header>
      <IonContent className='page-content' forceOverscroll>
        <IonRefresher slot='fixed' onIonRefresh={doRefresh}>
          <IonRefresherContent
            pullingIcon={chevronDownCircleOutline}
            pullingText={i18n.t('order.pullrefresh')}
          />
        </IonRefresher>
        {list.length === 0 ? <MktEmpty text={i18n.t('mkt.activityReserve.noRecords')} /> : (
          <div className='record-list'>
            {list.map((item: any) => {
              const storeName = getRecordStoreName(item)
              const sessionTime = formatDateRange(getRecordSessionStartAt(item), getRecordSessionEndAt(item))
              return (
                <div
                  className='record-card'
                  key={getBookingId(item)}
                  onClick={() => history.push({
                    pathname: `/activityReserve/record/${getBookingId(item)}`,
                    state: { bookingStatus: typeRef.current }
                  })}
                >
                  <div className='record-cover' style={{ backgroundImage: `url(${getCover(item) || defaultImg})` }}></div>
                  <div className='record-info'>
                    <div className='record-title'>{getActivityName(item)}</div>
                    {sessionTime !== '--' && <div className='record-line'>{sessionTime}</div>}
                    {storeName && <div className='record-line'>{storeName}</div>}
                    <div className='record-line record-quantity-line'><em>{i18n.t('mkt.activityReserve.quantityMultiplier', { count: getTicketQuantity(item) })}</em></div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
        {hasMore && (
          <IonInfiniteScroll threshold='100px' onIonInfinite={onScrollEnd}>
            <IonInfiniteScrollContent
              loadingSpinner='bubbles'
              loadingText={i18n.t('order.loadmore')}
            />
          </IonInfiniteScroll>
        )}
      </IonContent>
    </IonPage>
  )
}

export default observer(ActivityReserveRecords)
